import { Grid, Theme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FlipToBackOutlinedIcon from '@mui/icons-material/FlipToBackOutlined';
import { Check, Lock } from '@globalvisioninc/assets/icons';
import { Select, type SelectOptionGroup } from '@globalvisioninc/ui/select';
import { AllDocumentTypes, PageWidthFit, ZoomLevelLimits } from 'types';
import { GVButton } from 'components/common';
import { usePDFZoom } from 'pdftron';
import { menuLightGrey } from 'styles/vars';
import { makeStyles } from 'tss-react/mui';
import { useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { useZoomStateStore } from 'zstore/zoomStateStore';
import { trackEvent } from '../../../components/Tracker/TrackerProvider';
import styles from './ZoomControls.module.css';

const ZOOM_LOCK_VALUE = 'lock';
const ZOOM_DROPDOWN_OPTIONS = [10, 25, 50, 100, 125, 150, 200, 400, 800];

interface ZoomControlsProps {
  document: AllDocumentTypes;
}
interface CustomWindow extends Window {
  'zoom-screen-tracking-active'?: boolean;
}

const useStyles = makeStyles()((theme: Theme) => {
  return {
    zoomButton: {
      backgroundColor: menuLightGrey,
      minWidth: 'auto',
      width: theme.spacing(4),
      height: theme.spacing(4),
    },
    marqueeZoomButton: {
      minWidth: 'auto',
      width: theme.spacing(4),
      height: theme.spacing(4),
      '&.selected': {
        backgroundColor: '#d32f2f',
        '&:hover': {
          backgroundColor: '#b71c1c',
        },
      },
      '&.unselected': {
        backgroundColor: menuLightGrey,
        '&:hover': {
          backgroundColor: '#3a3d42',
        },
      },
    },
  };
});

const ZoomControls = (props: ZoomControlsProps) => {
  const { document } = props;
  const { classes } = useStyles();
  const { setZoomLockLevel } = useZoomStateStore();

  const trackScreenResolution = useCallback(() => {
    trackEvent({
      name: 'screenResolutionDetection',
      data: {
        width: window.innerWidth,
        height: window.innerHeight,
        document,
      },
    });
  }, [document]);

  useEffect(() => {
    const trackingKey = 'zoom-screen-tracking-active';
    if ((window as CustomWindow)[trackingKey]) return;

    (window as CustomWindow)[trackingKey] = true;
    let debounceTimeout: NodeJS.Timeout;

    const handleResize = () => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(trackScreenResolution, 200);
    };

    window.addEventListener('resize', handleResize);
    trackScreenResolution();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (debounceTimeout) clearTimeout(debounceTimeout);
      delete (window as CustomWindow)[trackingKey];
    };
  }, [trackScreenResolution]);

  const {
    zoomOption,
    zoomLockLevel,
    changeDocumentZoom,
    changeDocumentPageFitMode,
    documentZoom,
    toggleMarquee,
    marqueeSelected,
  } = usePDFZoom(document);

  const handleZoom = (direction: 'in' | 'out'): void => {
    let stepValue = Math.floor(documentZoom / 5);

    if (direction === 'in' && documentZoom !== ZoomLevelLimits.maxZoom * 100) {
      const newZoomedInValue = Math.min(documentZoom + stepValue, ZoomLevelLimits.maxZoom * 100);
      changeDocumentZoom(newZoomedInValue);
    } else if (direction === 'out' && documentZoom !== ZoomLevelLimits.minZoom * 100) {
      stepValue = Math.max(stepValue, 1);
      const newZoomedOutValue = Math.max(documentZoom - stepValue, ZoomLevelLimits.minZoom * 100);
      changeDocumentZoom(newZoomedOutValue);
    }
  };

  const handleZoomLock = () => {
    const zoom = zoomLockLevel ? null : zoomOption || documentZoom;
    setZoomLockLevel(document, zoom);
    trackEvent({
      name: zoomLockLevel ? 'lockZoomDisabled' : 'lockZoomEnabled',
      data: {
        document,
        zoomLevel: zoom,
      },
    });
  };

  // Fit mode is the only controlled value; percentages are shown via renderValue (same as next).
  const selectValue = zoomOption || null;

  const zoomOptions: SelectOptionGroup[] = [
    {
      options: [
        { label: 'Fit Width', value: PageWidthFit.fitWidth },
        { label: 'Fit Page', value: PageWidthFit.fitPage },
      ],
    },
    {
      options: ZOOM_DROPDOWN_OPTIONS.map((value) => ({
        label: `${value}%`,
        value: String(value),
      })),
    },
    {
      options: [
        {
          label: 'Lock Zoom',
          value: ZOOM_LOCK_VALUE,
          endSlot: zoomLockLevel ? <Check /> : undefined,
        },
      ],
    },
  ];

  return (
    <Grid container wrap="nowrap" spacing={0.5}>
      <Grid item>
        <GVButton
          id={`${document}-Zoom-out`}
          tooltipTitle="Zoom Out"
          action={() => handleZoom('out')}
          className={classes.zoomButton}
        >
          <RemoveIcon fontSize="small" />
        </GVButton>
      </Grid>
      <Grid item>
        <GVButton
          id={`${document}-Zoom-in`}
          tooltipTitle="Zoom In"
          action={() => handleZoom('in')}
          className={classes.zoomButton}
        >
          <AddIcon fontSize="small" />
        </GVButton>
      </Grid>
      <Grid item>
        <GVButton
          id={`${document}-Marquee-zoom`}
          tooltipTitle="Marquee Zoom"
          action={() => {
            toggleMarquee();
            trackEvent({
              name: marqueeSelected ? 'marqueeZoomDisabled' : 'marqueeZoomEnabled',
              data: {
                document,
              },
            });
          }}
          className={classNames(classes.marqueeZoomButton, {
            selected: marqueeSelected,
            unselected: !marqueeSelected,
          })}
        >
          <FlipToBackOutlinedIcon fontSize="small" />
        </GVButton>
      </Grid>
      <Grid item className={styles.toolbarSelect}>
        <Select.Root
          id={`select-width-option-${document}`}
          label="Zoom"
          isLabelHidden
          value={selectValue}
          onValueChange={(nextValue) => {
            if (nextValue === ZOOM_LOCK_VALUE) {
              handleZoomLock();
              return;
            }
            if (Object.values(PageWidthFit).includes(nextValue as PageWidthFit)) {
              changeDocumentPageFitMode(nextValue as PageWidthFit);
              return;
            }
            changeDocumentZoom(Number(nextValue));
          }}
          renderValue={(option) => (
            <>
              <span data-testid="selectedZoomLevel">{option?.label ?? `${documentZoom}%`}</span>
              {zoomLockLevel ? <Lock /> : null}
            </>
          )}
        >
          <Select.Trigger className={styles.zoomSelect} />
          <Select.Portal>
            <Select.Content options={zoomOptions} align="start" sideOffset={8} className={styles.toolbarSelectPanel}>
              <Select.Arrow align="start" />
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </Grid>
    </Grid>
  );
};

export default ZoomControls;
